<template>
  <transition name="fade">
    <div :class="['zs-toast', severity]">
      <div class="zs-toast-icon">
        <ZSIcon color="white" :icon="icon" />
      </div>
      <div class="zs-toast-content">
        <div class="zs-toast-summary">{{ toast.summary }}</div>
        <div class="zs-toast-detail">{{ toast.detail }}</div>
      </div>
      <div class="zs-toast-close-icon">
        <ZSButton @click="close" class="text">
          <ZSIcon color="white" icon="Close" />
        </ZSButton>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import ZSIcon from '../global/ZSIcon.vue';
import ZSButton from '../ZSButton.vue';

const props = defineProps({
  toast: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);

// Accept both `type` and `severity` (callers use either); default to 'info' so a
// toast always gets a styled background instead of rendering transparent.
const severity = computed(() => props.toast.type ?? props.toast.severity ?? 'info');

const icon = computed(() => {
  switch (severity.value) {
    case 'success':
      return 'CheckmarkCircle';
    case 'error':
      return 'WarningTriangle';
    case 'info':
      return 'Info';
    case 'warn':
      return 'Warning';
    default:
      return 'Info';
  }
});

const close = () => {
  emit('close');
};

onMounted(() => {
  if (props.toast.life) {
    setTimeout(() => {
      close();
    }, props.toast.life);
  }
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.zs-toast {
  display: flex;
  align-items: center;
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 6px;
  color: #fff;
}

.zs-toast.success {
  background-color: #28a745;
}

.zs-toast.error {
  background-color: #dc3545;
}

.zs-toast.info {
  background-color: #17a2b8;
}

.zs-toast.warn {
  background-color: #ffc107;
  color: #212529;
}

.zs-toast-icon {
  margin-right: 1rem;
}

.zs-toast-content {
  flex-grow: 1;
}

.zs-toast-summary {
  font-weight: bold;
}

.zs-toast-close-icon {
  cursor: pointer;
  margin-left: 1em;
  align-self: start;
}
</style>
